import React, { memo, useCallback, useMemo } from 'react';

import { type InternalRefetchQueriesInclude } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { Alert } from '@mui/material';
import { HouseIcon, MapPlusIcon } from 'lucide-react';
import {
  type DeepPartial,
  type ErrorOption,
  type FieldPath,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  Navigate,
  Route,
  Routes,
  useMatch,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type {
  GetCreateTransactionDataQuery,
  GetPropertyTypesQuery,
  Properties_Insert_Input,
  Property_Transactions_Insert_Input,
} from '@graphql/__generated__/graphql';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import {
  type DrawerTab,
  generateTab,
} from '@modules/_shared/components/drawer/DrawerTopBar';
import type { RaAddressType } from '@modules/_shared/components/form/RaAddressInput';
import {
  type FormDefinitionType,
  type FormFieldDefinitionType,
  type NonInternalProps,
  RaForm,
  createCategoryElement,
} from '@modules/_shared/components/form/RaForm';
import {
  type ListingPreview,
  RaListingPreview,
  type RaListingPreviewData,
} from '@modules/_shared/components/form/RaListingPreview';
import type { PropertyTypeLight } from '@modules/_shared/components/property-form/propertyFormQueries';
import { useTransactionPropertyTypeSelection } from '@modules/_shared/hooks/useTransactionPropertyTypeSelection';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  createTransactionPropertyGroupOptions,
  createTransactionPropertyTypeOptions,
} from '@modules/_shared/utils/propertyTypeHelpers';
import {
  brokerTeamToFollow,
  isSellerTeamLocked,
} from '@modules/_shared/utils/transactionHelpers';
import {
  clearInvalidTransactionPropertyDetailFields,
  isTransactionPropertyDetailFieldVisible,
} from '@modules/_shared/utils/transactionPropertyDetails';
import { numberContainsDecimals } from '@modules/_shared/utils/validation';
import {
  type TransactionFormContext,
  type TransactionFormData,
  transactionFormDefinition,
} from '@modules/transactions/TransactionFormDefinition';
import {
  CREATE_TRANSACTION,
  GET_USER_DEFAULT_TEAM_ID,
} from '@modules/transactions/transactionQueries';
import { useLocale } from '@shared/hooks/locale';

type PropertyTypes = GetPropertyTypesQuery['property_types'];
type CreateTransactionListingFormData = Omit<
  TransactionFormData,
  'sellers_ids' | 'buyers_ids' | 'created_by' | 'created_at'
> &
  RaListingPreviewData;
type CreateTransactionScratchFormData = Omit<
  TransactionFormData,
  'sellers_ids' | 'buyers_ids' | 'created_by' | 'created_at'
> & {
  __composite_type: string;
  __property_type?: PropertyTypeLight;
  property_type_id: Properties_Insert_Input['property_type_id'];
  property: Pick<
    Properties_Insert_Input,
    | 'number_of_bedrooms'
    | 'number_of_rooms'
    | 'built_surface'
    | 'living_surface'
    | 'land_surface'
    | 'usable_surface'
    | Exclude<keyof RaAddressType, 'google_place_id'>
  >;
};
type CreateTransactionFormData =
  | CreateTransactionListingFormData
  | CreateTransactionScratchFormData;
type CreateTransactionFormContext = Omit<
  TransactionFormContext,
  'includeMetadata' | 'onUserCreateSelected'
> & {
  propertyTypes: PropertyTypes;
  isTenantSpanish: boolean;
  countryCode: string;
  fromListing: boolean;
  freezeListingChoice: boolean;
  fromOffer: string | null;
};

const GET_CREATE_TRANSACTION_DATA_QUERY = gql(/* GraphQL */ `
  query GetCreateTransactionData(
    $includeListing: Boolean!
    $includeOffer: Boolean!
    $offerId: uuid
    $listingId: uuid
  ) {
    lots(where: { id: { _eq: $listingId } }) @include(if: $includeListing) {
      id
      broker_id
      mandate_type
      broker {
        id
        default_team_id
      }
      ...ListingCardSnippet
      last_accepted_offer: offers(where: { id: { _eq: $offerId } })
        @include(if: $includeOffer) {
        id
        amount
        buyer_id
      }
    }
    property_types {
      id
      name
      label
      main_type
      composite_type
      type
    }
  }
`);

const TransactionPropertyTypeProvider: React.FC<{
  propertyTypes: PropertyTypes;
}> = ({ propertyTypes }) => {
  const { setValue } = useFormContext<CreateTransactionScratchFormData>();
  const compositeType = useWatch<
    CreateTransactionScratchFormData,
    '__composite_type'
  >({ name: '__composite_type' });
  const propertyTypeId = useWatch<
    CreateTransactionScratchFormData,
    'property_type_id'
  >({ name: 'property_type_id' });

  const handleInvalidSelection = useCallback(() => {
    setValue('property_type_id', null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('__property_type', null);
  }, [setValue]);

  const handleSelectedPropertyType = useCallback(
    (selectedPropertyType: PropertyTypeLight | undefined) => {
      if (selectedPropertyType == null) {
        setValue('__property_type', null);
        return;
      }

      setValue('__property_type', {
        main_type: selectedPropertyType.main_type,
        name: selectedPropertyType.name,
      });

      clearInvalidTransactionPropertyDetailFields(
        selectedPropertyType,
        field => {
          setValue(`property.${field}`, undefined);
        },
      );
    },
    [setValue],
  );

  useTransactionPropertyTypeSelection({
    propertyTypes,
    propertyTypeId,
    compositeType,
    onInvalidSelection: handleInvalidSelection,
    onSelectedPropertyType: handleSelectedPropertyType,
  });

  return null;
};

const RaListingPreviewWrapper: React.FC<{
  freezeListingChoice: boolean;
  fromOffer: string | null;
}> = memo(({ freezeListingChoice, fromOffer }) => {
  const { setValue, getValues } = useFormContext<CreateTransactionFormData>();
  const purchasePrice = getValues('purchase_price');

  const onListingFetched = useCallback(
    (listing: ListingPreview) => {
      if (
        fromOffer != null &&
        listing?.last_accepted_offer != null &&
        listing.last_accepted_offer.length > 0 &&
        purchasePrice == null
      ) {
        setValue('purchase_price', listing.last_accepted_offer[0].amount, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      // The listing's broker wins over the signed-in default; the team follows
      // through the broker-change handler.
      if (listing?.broker_id != null) {
        setValue('seller_broker_id', listing.broker_id, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    [fromOffer, purchasePrice, setValue],
  );

  const listingLink = useMemo(
    () =>
      freezeListingChoice
        ? undefined
        : (listingId: string) => ({
            pathname: `/listings/${listingId}`,
          }),
    [freezeListingChoice],
  );

  return (
    <RaListingPreview
      includeOfferInQuery={fromOffer ?? undefined}
      onListingFetched={onListingFetched}
      listingLink={listingLink}
    />
  );
});

const formDefinition: FormDefinitionType<
  CreateTransactionFormData,
  CreateTransactionFormContext
> = ({ t, context }) => {
  const {
    fromListing,
    freezeListingChoice,
    fromOffer,
    ...transactionFormContext
  } = context;
  const fieldDefinitions: FormFieldDefinitionType<CreateTransactionFormData>[] =
    [];

  if (fromListing) {
    fieldDefinitions.push(
      ...([
        {
          name: 'listing_title',
          label: t('Listing'),
          type: 'category-title',
        },
        {
          type: 'lot',
          name: 'listing_id',
          label: t('Listing'),
          disabled: () => freezeListingChoice || context.readonly,
        },
        {
          type: 'custom',
          name: 'listingPreview',
          element: (
            <RaListingPreviewWrapper
              freezeListingChoice={freezeListingChoice}
              fromOffer={fromOffer}
            />
          ),
          gridProps: { md: 12 },
        },
      ] as FormFieldDefinitionType<CreateTransactionFormData>[]),
    );
  } else {
    fieldDefinitions.push(createCategoryElement('address', t('Address')), {
      path: 'property',
      label: t('Name'),
      type: 'address',
      countryRestriction: context.countryCode,
      gridProps: { xs: 12, md: 12, lg: 12, xl: 12 },
    });
  }

  fieldDefinitions.push(
    ...(transactionFormDefinition({
      t,
      context: {
        ...transactionFormContext,
        includeMetadata: false,
        includeSellers: false,
        includeBuyers: false,
      },
    }) as FormFieldDefinitionType<CreateTransactionFormData>[]),
  );

  if (!fromListing) {
    fieldDefinitions.push(
      ...([
        createCategoryElement('property_details_title', t('Property details')),
        {
          name: '__composite_type',
          label: t('Property main type'),
          type: 'select',
          options: () =>
            createTransactionPropertyGroupOptions(context.propertyTypes),
          required: true,
        },
        {
          name: 'property_type_id',
          label: t('Property type'),
          type: 'select',
          options: ({ __composite_type }: CreateTransactionScratchFormData) =>
            createTransactionPropertyTypeOptions(
              context.propertyTypes,
              __composite_type,
            ),
          required: true,
        },
        {
          name: context.isTenantSpanish
            ? 'property.number_of_bedrooms'
            : 'property.number_of_rooms',
          label: context.isTenantSpanish
            ? t('Number of bedrooms')
            : t('Number of rooms'),
          type: 'number',
          decimalNumbers: 1,
          render: ({ __property_type }: CreateTransactionScratchFormData) => {
            return isTransactionPropertyDetailFieldVisible(
              __property_type ?? undefined,
              context.isTenantSpanish
                ? 'number_of_bedrooms'
                : 'number_of_rooms',
            );
          },
          required: true,
          min: 1,
        },
        {
          name: context.isTenantSpanish
            ? 'property.built_surface'
            : 'property.living_surface',
          label: context.isTenantSpanish
            ? t('Built surface')
            : t('Living surface'),
          type: 'number',
          render: ({ __property_type }: CreateTransactionScratchFormData) => {
            return isTransactionPropertyDetailFieldVisible(
              __property_type ?? undefined,
              context.isTenantSpanish ? 'built_surface' : 'living_surface',
            );
          },
          required: true,
          min: 0,
        },
        {
          name: 'property.usable_surface',
          label: t('Usable surface'),
          type: 'number',
          render: ({ __property_type }: CreateTransactionScratchFormData) => {
            return isTransactionPropertyDetailFieldVisible(
              __property_type ?? undefined,
              'usable_surface',
            );
          },
          required: true,
          min: 0,
        },
        {
          name: 'property.land_surface',
          label: t('Land surface'),
          type: 'number',
          render: ({ __property_type }: CreateTransactionScratchFormData) => {
            return isTransactionPropertyDetailFieldVisible(
              __property_type ?? undefined,
              'land_surface',
            );
          },
          required: true,
          min: 0,
        },
      ] as FormFieldDefinitionType<CreateTransactionFormData>[]),
    );
  }

  return fieldDefinitions;
};

const CreateTransactionForm: React.FC<{
  propertyTypes: PropertyTypes;
  handleSubmit: (
    values: NonInternalProps<CreateTransactionFormData>,
    rawValues: CreateTransactionFormData,
  ) => Promise<any>;
  fromListing: boolean;
  onCancel: () => void;
  defaultListing?:
    | NonNullable<GetCreateTransactionDataQuery['lots']>[number]
    | null;
  freezeListingChoice?: boolean;
  fromOffer?: string | null;
}> = ({
  propertyTypes,
  handleSubmit,
  fromListing,
  onCancel,
  defaultListing,
  freezeListingChoice = false,
  fromOffer = null,
}) => {
  const { me } = useAppData();
  const { t, countryCode } = useLocale();

  const [getTeam] = useLazyQuery(GET_USER_DEFAULT_TEAM_ID);
  const defaultValues = useMemo<DeepPartial<CreateTransactionFormData>>(() => {
    // Only a broker can be the seller agent; admins start with an empty agent
    // but their team, so the field still lists that team's brokers on click.
    const onlyTeamId =
      me?.teams?.length === 1 ? me.teams[0]?.team_id : undefined;
    const sellerDefaults: DeepPartial<CreateTransactionFormData> = {
      seller_broker_id: me?.is_broker ? me.id : undefined,
      seller_broker_team_id: me?.default_team?.id ?? onlyTeamId,
    };

    if (!fromListing) {
      return { property: {}, __know_signature_date: true, ...sellerDefaults };
    }

    const defaultListingValues: DeepPartial<CreateTransactionFormData> = {
      listing_id: defaultListing?.id,
      __know_signature_date: true,
      ...sellerDefaults,
    };

    if (defaultListing != null) {
      if (
        fromOffer != null &&
        defaultListing?.last_accepted_offer != null &&
        defaultListing.last_accepted_offer.length > 0
      ) {
        defaultListingValues.purchase_price =
          defaultListing.last_accepted_offer[0].amount;
      }

      if (defaultListing?.broker_id != null) {
        defaultListingValues.seller_broker_id = defaultListing.broker_id;
        defaultListingValues.seller_broker_team_id =
          defaultListing.broker?.default_team_id ??
          sellerDefaults.seller_broker_team_id;
      }
    }

    return defaultListingValues;
  }, [
    fromListing,
    defaultListing,
    fromOffer,
    me?.id,
    me?.is_broker,
    me?.default_team?.id,
    me?.teams,
  ]);

  const context = useMemo<CreateTransactionFormContext>(
    () => ({
      propertyTypes,
      isTenantSpanish: me?.tenant.country_code === 'ES',
      countryCode,
      fullAccess: me?.is_admin ?? false,
      sellerTeamLocked: isSellerTeamLocked(me),
      readonly: false,
      fromListing,
      freezeListingChoice,
      fromOffer,
    }),
    [
      propertyTypes,
      me,
      countryCode,
      fromListing,
      freezeListingChoice,
      fromOffer,
    ],
  );

  return (
    <RaForm
      formDefinition={formDefinition}
      key={fromListing ? 'listing' : 'scratch'}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      validate={values => {
        const errors: [FieldPath<CreateTransactionFormData>, ErrorOption][] =
          [];

        if (
          'property' in values &&
          values.property.number_of_rooms != null &&
          !numberContainsDecimals(values.property.number_of_rooms, [0, 5])
        ) {
          errors.push([
            'property.number_of_rooms',
            {
              message: t('The number of rooms must be a 0.5 increment'),
            },
          ]);
        }

        if (values.purchase_price === 0) {
          errors.push([
            'purchase_price',
            {
              type: 'min',
              message: t('The purchase price must be greater than 0'),
            },
          ]);
        }

        return errors;
      }}
      context={context}
      onCancel={isDirty => !isDirty && onCancel()}
      onChange={(data, name, _, { setValue, getValues }) => {
        const followBrokerTeam = (
          brokerId: string | null | undefined,
          side: 'seller' | 'buyer',
        ) => {
          if (brokerId == null) {
            return;
          }
          const brokerField = `${side}_broker_id` as const;
          const teamField = `${side}_broker_team_id` as const;
          getTeam({ variables: { id: brokerId } }).then(response => {
            // A slower response for a broker the user already replaced must not win.
            if (getValues(brokerField) !== brokerId) {
              return;
            }
            const teamId = brokerTeamToFollow(
              response?.data?.users_by_pk,
              // Read at resolve time: the user may have changed the team meanwhile.
              getValues(teamField),
            );
            if (teamId !== undefined) {
              setValue(teamField, teamId, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
          });
        };

        if (name === 'seller_broker_id') {
          followBrokerTeam(data?.seller_broker_id, 'seller');
        } else if (name === 'buyer_broker_id') {
          followBrokerTeam(data?.buyer_broker_id, 'buyer');
        }
      }}
    >
      {!fromListing && (
        <TransactionPropertyTypeProvider propertyTypes={propertyTypes} />
      )}
    </RaForm>
  );
};

export const TransactionCreateDrawer = ({
  maxWidth = 960,
  refetchQueries,
  noTabs,
  onCreate,
  freezeListingChoice,
}: {
  maxWidth?: number;
  refetchQueries?: InternalRefetchQueriesInclude;
  noTabs?: boolean;
  onCreate?: (id: string) => void;
  freezeListingChoice?: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const [createTransaction] = useMutation(CREATE_TRANSACTION, {
    refetchQueries,
  });

  const defaultListingId = searchParams.get('selected-listing');
  const fromOffer = searchParams.get('from-offer');

  const { data, error } = useQuery(GET_CREATE_TRANSACTION_DATA_QUERY, {
    variables: {
      listingId: defaultListingId,
      includeListing: defaultListingId != null,
      offerId: fromOffer,
      includeOffer: fromOffer != null,
    },
  });
  const property_types = data?.property_types ?? [];
  const defaultListing = data?.lots?.[0] ?? null;

  const matchA = useMatch('/transactions/new/:tabName/*');
  const matchB = useMatch('/dashboard/add-transaction/:tabName/*');
  const currentTab =
    matchA?.params.tabName ?? matchB?.params.tabName ?? 'scratch';

  const tabsDef: [string, string, React.ReactElement?][] = [
    [t('From scratch'), 'scratch', <MapPlusIcon />],
    [t('From listing'), 'listing', <HouseIcon />],
  ];

  const tabs: DrawerTab[] | undefined = noTabs
    ? undefined
    : tabsDef.map(([label, value, icon]) =>
        generateTab([searchParams.toString(), label, value, icon]),
      );

  const handleClose = () => {
    searchParams.delete('selected-listing');
    searchParams.delete('from-offer');

    navigate({
      pathname: '../',
      search: searchParams.toString(),
    });
  };

  const handleSubmit = useCallback(
    async (
      values: NonInternalProps<CreateTransactionFormData>,
      rawValues: CreateTransactionFormData,
    ) => {
      let input: Property_Transactions_Insert_Input = {};

      if ('property' in values) {
        const { property, property_type_id, ...transaction } = values;
        input = {
          ...transaction,
          property: {
            data: {
              ...property,
              property_type_id,
            },
          },
        };
      } else {
        const { listing_id, ...transaction } = values;
        const selectedListing = (rawValues as CreateTransactionListingFormData)
          .__listing;

        const sellers = selectedListing?.sellers.map(({ user_id }) => ({
          user_id,
        }));

        const buyers =
          selectedListing?.last_accepted_offer?.[0].buyer_id != null
            ? [
                {
                  user_id: selectedListing?.last_accepted_offer?.[0].buyer_id,
                },
              ]
            : undefined;

        input = {
          ...transaction,
          lot_id: listing_id,
          property_id: selectedListing?.property.id,
          buyers: buyers != null ? { data: buyers } : undefined,
          sellers: sellers != null ? { data: sellers } : undefined,
        };
      }

      const res = await createTransaction({
        variables: {
          transaction: input,
        },
      });

      if (res.data?.insert_property_transactions_one?.id != null) {
        onCreate?.(res.data.insert_property_transactions_one.id);
      }
    },
    [createTransaction, onCreate],
  );

  return (
    <Drawer
      onClose={handleClose}
      currentTab={currentTab}
      title={t('Create transaction')}
      tabs={tabs}
      maxWidth={maxWidth}
    >
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      )}
      <Routes>
        <Route
          index
          element={
            <Navigate
              to={{
                pathname: 'scratch',
                search: searchParams.toString(),
              }}
            />
          }
        />
        <Route
          path="scratch"
          element={
            <CreateTransactionForm
              propertyTypes={property_types}
              handleSubmit={handleSubmit}
              fromListing={false}
              onCancel={handleClose}
            />
          }
        />
        <Route
          path="listing"
          element={
            <CreateTransactionForm
              propertyTypes={property_types}
              handleSubmit={handleSubmit}
              fromListing={true}
              onCancel={handleClose}
              defaultListing={defaultListing}
              freezeListingChoice={freezeListingChoice}
              fromOffer={fromOffer}
            />
          }
        />
      </Routes>
    </Drawer>
  );
};

export default TransactionCreateDrawer;
